import React, { useState } from "react";
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import clsx from "clsx";

interface EditableTableProps<T extends object> {
  data: T[];
  columns: ColumnDef<unknown, unknown>[];
  onDataChange?: (data: T[]) => void;
  columnClassName?: string;
}

export function EditableTable<T extends object>({
  data,
  columns,
  onDataChange,
  columnClassName,
}: EditableTableProps<T>) {
  const [tableData, setTableData] = useState<T[]>(data);

  const updateData = React.useCallback(
    (rowIndex: number, columnId: string, value: any) => {
      setTableData((old) =>
        old.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
      onDataChange?.(
        tableData.map((row, i) =>
          i === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    },
    [onDataChange, tableData]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      updateData,
    },
  });

  return (
    <div className="overflow-hidden border border-gray-300 rounded-md">
      <table className="min-w-full">
        <thead className="bg-gray-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b border-gray-300">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={clsx(
                    "border-r border-gray-300 px-2 py-1 text-left text-sm font-medium",
                    "text-gray-900 last-of-type:border-r-0 "
                  )}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-gray-300 last-of-type:border-b-0"
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={clsx(
                    "border-gray-300 p-0 text-sm font-normal text-gray-900",
                    "border-r last-of-type:border-r-0",
                    columnClassName
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
